import { forwardRef, memo, useEffect, useRef, useState } from "react";

import { useUserAuthenticationContext } from "@/src/contexts";
import { getCookie } from "@Components/Modules/Helpers";
import type { PropsWithStyleX } from "@stylex/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

const styles = stylex.create({
    container: {
        textAlign: "center",
    },
});

type SingleSizeArray = number[];
type MultiSizeArray = SingleSizeArray | SingleSizeArray[];
export type IAdTagViewPortSizes = { viewport: SingleSizeArray; slot: MultiSizeArray }[];

export type IAdTag = PropsWithStyleX & {
    layoutAdUnitPath?: string;
    adUnitId?: string;
    slugTarget?: string;
    closeOnLoadFail?: boolean;
    sendUserInfo?: boolean;
    optId: string;
    viewportSizes?: IAdTagViewPortSizes;
    onSlotRender?: (e: googletag.events.SlotRenderEndedEvent) => void;
};

const defaultViewportSizes: IAdTagViewPortSizes = [
    {
        viewport: [950, 0],
        slot: [
            [750, 200],
            [750, 300],
            [950, 200],
            [950, 300],
            [970, 300],
            [300, 250],
        ],
    },
    {
        viewport: [750, 0],
        slot: [
            [640, 100],
            [640, 200],
            [750, 200],
            [300, 250],
        ],
    },
    {
        viewport: [640, 0],
        slot: [
            [360, 100],
            [640, 100],
            [640, 200],
            [300, 250],
        ],
    },
    {
        viewport: [360, 0],
        slot: [
            [360, 50],
            [360, 100],
            [300, 250],
        ],
    },
    {
        viewport: [320, 0],
        slot: [
            [320, 50],
            [320, 100],
            [300, 250],
        ],
    },
];

const renderSlotMap = new Map<string, (e: googletag.events.SlotRenderEndedEvent) => void>();

const onRenderSlot = (e: googletag.events.SlotRenderEndedEvent) => {
    const renderCallback = renderSlotMap.get(e.slot.getSlotElementId());
    if (renderCallback) {
        renderCallback(e);
    }
};

const AdTag = memo(
    forwardRef<HTMLElement, IAdTag>((props, ref) => {
        const refSlot = useRef<googletag.Slot | null>();
        const timeoutRef = useRef<number>();
        const [widthWindow, setWidthWindow] = useState<number>(0);
        const { isAuthenticated } = useUserAuthenticationContext();

        const allSlotSizes = [
            [300, 250],
            [320, 50],
            [320, 100],
            [320, 450],
            [320, 480],
            [360, 50],
            [360, 100],
            [470, 661],
            [470, 705],
            [640, 200],
            [640, 100],
            [640, 900],
            [640, 960],
            [750, 100],
            [750, 300],
            [750, 200],
            [950, 200],
            [970, 300],
        ];

        const handleResize = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = window.setTimeout(setWidthWindow, 200, window.innerWidth);
        };

        const removeSlot = () => {
            if (refSlot.current) {
                googletag.pubads().clear([refSlot.current]);
                googletag.destroySlots([refSlot.current]);
                refSlot.current = null;
            }

            renderSlotMap.delete(props.optId);
        };

        // https://developers.google.com/publisher-tag/reference#googletag.PubAdsService_enableLazyLoad
        useEffect(() => {
            window.addEventListener("resize", handleResize);

            if (window.googletag && !window.googletag.pubadsReady) {
                window.googletag.cmd.push(() => {
                    window.googletag.pubads().addEventListener("slotRenderEnded", onRenderSlot);
                });
            }
            return () => {
                removeSlot();
                window.removeEventListener("resize", handleResize);
            };
        }, []);

        useEffect(() => {
            const { googletag } = window;
            const shouldCollapse = props.closeOnLoadFail;

            if (props.onSlotRender) {
                renderSlotMap.set(props.optId, props.onSlotRender);
            }

            googletag?.cmd?.push(() => {
                googletag.pubads().collapseEmptyDivs(shouldCollapse);
                refSlot.current = googletag
                    .defineSlot(
                        props.adUnitId ??
                            `/${process.env.AD_UNIT}/${process.env.AD_ENV}_goodiepage_${props.layoutAdUnitPath}_top_0`,
                        allSlotSizes,
                        props.optId,
                    )
                    .addService(googletag.pubads())
                    .setCollapseEmptyDiv(shouldCollapse, false);

                if (props.slugTarget) {
                    refSlot.current.setTargeting("ps", props.slugTarget);
                }

                if (props.sendUserInfo) {
                    const cookieUserId = getCookie("userId");

                    refSlot.current.setTargeting("uid", cookieUserId);
                    refSlot.current.setTargeting("lgdin", `${isAuthenticated}`);
                }

                const sizeMapping = googletag.sizeMapping();

                const mySizes = props.viewportSizes || defaultViewportSizes;

                mySizes.forEach((config) => sizeMapping.addSize(config.viewport, config.slot));

                refSlot.current.defineSizeMapping(sizeMapping.build());
                googletag.pubads().disableInitialLoad();
                googletag.enableServices();
            });

            googletag?.cmd?.push(() => {
                if (refSlot.current) {
                    googletag.display(props.optId);
                    googletag.pubads().refresh([refSlot.current], { changeCorrelator: true });
                }
            });

            return removeSlot;
        }, [props.layoutAdUnitPath, props.adUnitId, props.optId, props.slugTarget, widthWindow]);

        return <aside {...stylex.props(styles.container, props.styleX)} id={props.optId} ref={ref} />;
    }),
    (prevProps: IAdTag, nextProps: IAdTag) => {
        if (
            prevProps.sendUserInfo === nextProps.sendUserInfo &&
            prevProps.layoutAdUnitPath === nextProps.layoutAdUnitPath &&
            prevProps.slugTarget !== nextProps.slugTarget &&
            nextProps.slugTarget
        ) {
            return false;
        }

        return nextProps.layoutAdUnitPath === prevProps.layoutAdUnitPath;
    },
);

export default AdTag;
